import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Lead {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  status: string;
  description: string;
  converted: boolean;
}

// Update allowedFields to include name searching capability
const allowedFields = ['email', 'phone', 'name']; // Added 'name' as a virtual field

// Fix environment variables for Vite
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

async function getLeadByField(field: string, value: string): Promise<Lead | null> {
  if (!allowedFields.includes(field)) {
    console.error('Invalid field:', field);
    return null;
  }

  // Special handling for name searches
  if (field === 'name') {
    // Try to find by combining first_name and last_name
    const { data, error } = await supabase
      .from('leads')
      .select('first_name, last_name, email, phone, company, status, description, converted')
      .or(`first_name.ilike.%${value}%,last_name.ilike.%${value}%`);

    if (error) {
      console.error('Error fetching lead by name:', error);
      return null;
    }

    if (data && data.length > 0) {
      // Return the first matching lead
      return data[0] as Lead;
    }
    return null;
  }

  // Normal field search for email, phone, etc.
  const { data, error } = await supabase
    .from('leads')
    .select('first_name, last_name, email, phone, company, status, description, converted')
    .eq(field, value)
    .single();

  if (error) {
    console.error('Error fetching lead:', error);
    return null;
  }

  return data as Lead;
}

function formatLead(lead: Lead): string {
  return `
Lead Details:
First Name: ${lead.first_name}
Last Name: ${lead.last_name}
Email: ${lead.email}
Phone: ${lead.phone}
Company: ${lead.company}
Status: ${lead.status}
Description: ${lead.description}
Converted: ${lead.converted ? 'Yes' : 'No'}
  `.trim();
}

// Add this new function to break the circular dependency
async function callAnthropicAPI(prompt: string): Promise<string> {
  try {
    console.log('Calling Anthropic API with prompt:', prompt);
    
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message: prompt }),
    });

    // Only read the response body once
    const responseText = await response.text();
    let responseData;
    
    try {
      // Try to parse as JSON
      responseData = JSON.parse(responseText);
    } catch (e) {
      // If not valid JSON, use the text directly
      console.error('Failed to parse response as JSON:', responseText);
      
      if (!response.ok) {
        throw new Error(`Server error (${response.status}): ${responseText}`);
      }
      
      // If response was OK but not JSON, return the text
      return responseText;
    }

    // Handle error responses with JSON format
    if (!response.ok) {
      console.error('API error:', response.status, responseData);
      throw new Error(`Server error (${response.status}): ${responseData.error || responseData.message || 'Unknown error'}`);
    }

    console.log('Received response from Claude API:', responseData);
    return responseData.response || "No response content";
  } catch (error) {
    console.error('Error calling Anthropic API:', error);
    return `Sorry, I encountered an error: ${error.message}. Please try again later.`;
  }
}

async function handleUserQuery(query: string): Promise<string> {
  // Update the prompt to be more specific about field names
  const initialPrompt = `
You are an assistant named EZ-Assistant that can access a database of leads. If the user's query requires information from the database, respond with "DB_QUERY: field=value", where field is one of:
- email (for exact email address matches)
- phone (for exact phone number matches)
- name (for searching by a person's name)

Examples:
- If user asks about a lead by email, respond with: "DB_QUERY: email=user@example.com"
- If user asks about a lead by name, respond with: "DB_QUERY: name=John Smith"
- If user asks about a lead by phone, respond with: "DB_QUERY: phone=1234567890"

If the query does not require database access, respond normally without the DB_QUERY prefix.

User query: "${query}"
  `.trim();

  const initialResponse = await callAnthropicAPI(initialPrompt); // Use callAnthropicAPI instead

  // Step 2: Check if Claude indicates a database query
  if (initialResponse.startsWith('DB_QUERY:')) {
    const queryParams = initialResponse.split(':')[1].trim();
    const [field, value] = queryParams.split('=');
    const lead = await getLeadByField(field.trim(), value.trim());

    if (lead) {
      // Step 3: Format lead data and ask Claude for a response
      const leadText = formatLead(lead);
      const finalPrompt = `
The user asked: "${query}"

Here is the lead information:
${leadText}

Please provide a response based on this information.
      `.trim();
      return await callAnthropicAPI(finalPrompt); // Use callAnthropicAPI instead
    } else {
      return "Sorry, I couldn't find that lead.";
    }
  } else {
    // Step 4: Return Claude's response directly if no database query is needed
    return initialResponse;
  }
}

async function processMessage(message: string): Promise<string> {
  return await handleUserQuery(message);
}

const ChatWindow: React.FC<ChatWindowProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat window opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Keep the original callClaudeAPI for user messages but update it to use processMessage
  const callClaudeAPI = async (userMessage: string) => {
    try {
      console.log('Calling Claude API with message:', userMessage);
      
      // Use processMessage function to handle user queries
      return await processMessage(userMessage);
    } catch (error) {
      console.error('Error calling Claude API:', error);
      return `Sorry, I encountered an error: ${error.message}. Please try again later.`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Call Claude API instead of using setTimeout
    const claudeResponse = await callClaudeAPI(inputValue);
    
    const assistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      text: claudeResponse,
      sender: 'assistant',
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col border border-gray-200 dark:border-gray-700 z-50">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">EZ-Assistant</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                message.sender === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
              }`}
            >
              <p>{message.text}</p>
              <span className="text-xs opacity-75 mt-1 block">
                {message.timestamp.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </span>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-lg px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              <p className="animate-pulse">EZ-Assistant is thinking...</p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input form */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex space-x-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 p-2 
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                     disabled:bg-indigo-400 disabled:cursor-not-allowed"
            disabled={isLoading || !inputValue.trim()}
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatWindow;